import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Box, Button, Skeleton, CircularProgress, TextField, MenuItem } from '@mui/material';

const LoadingSkeleton = () => (
  <>
    <Skeleton variant="text" width={200} height={20} />
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Skeleton variant="rectangular" width={70} height={32} />
      <Skeleton variant="text" width={80} height={20} />
      <Skeleton variant="rectangular" width={50} height={32} />
    </Box>
  </>
);

const PAGE_SIZE_OPTIONS = [
  { value: 15, label: '15 per page' },
  { value: 30, label: '30 per page' },
  { value: 50, label: '50 per page' },
  { value: 100, label: '100 per page' },
  { value: 200, label: '200 per page' }
];

const PaginationContent = ({ pagination, isLoadingPaginated, fetchPaginatedData, accountNameFilter, filteredCount, totalFilteredCount, hasActiveFilters, pageSize, onPageSizeChange }) => {
  const startIcon = isLoadingPaginated ? <CircularProgress size={16} /> : null;
  const accountsOnPage = hasActiveFilters ? filteredCount : Math.min(pagination.limit, pagination.totalCount - ((pagination.currentPage - 1) * pagination.limit));
  const totalCount = hasActiveFilters ? (totalFilteredCount || pagination.totalCount) : pagination.totalCount;
  
  return (
    <>
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        Displaying {accountsOnPage} of {totalCount} accounts
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            size="small" 
            disabled={!pagination.hasPreviousPage || isLoadingPaginated}
            onClick={() => fetchPaginatedData(pagination.currentPage - 1, accountNameFilter, pageSize)}
            startIcon={startIcon}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ px: 2, py: 1 }}>
            Page {pagination.currentPage} of {pagination.totalPages}
          </Typography>
          <Button 
            size="small" 
            disabled={!pagination.hasNextPage || isLoadingPaginated}
            onClick={() => fetchPaginatedData(pagination.currentPage + 1, accountNameFilter, pageSize)}
            startIcon={startIcon}
          >
            Next
          </Button>
        </Box>
        <TextField
          select
          size="small"
          value={pageSize}
          onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
          disabled={isLoadingPaginated}
          sx={{ minWidth: 120 }}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </>
  );
};

PaginationContent.propTypes = {
  pagination: PropTypes.object.isRequired,
  isLoadingPaginated: PropTypes.bool.isRequired,
  fetchPaginatedData: PropTypes.func.isRequired,
  accountNameFilter: PropTypes.string.isRequired,
  filteredCount: PropTypes.number.isRequired,
  totalFilteredCount: PropTypes.number,
  hasActiveFilters: PropTypes.bool.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageSizeChange: PropTypes.func.isRequired
};

const PaginationControls = ({ pagination, isLoadingPaginated, fetchPaginatedData, accountNameFilter, filteredCount, totalFilteredCount, hasActiveFilters, pageSize, onPageSizeChange }) => {
  const renderContent = () => {
    if (isLoadingPaginated) {
      return <LoadingSkeleton />;
    }
    if (pagination) {
      return (
        <PaginationContent 
          pagination={pagination}
          isLoadingPaginated={isLoadingPaginated}
          fetchPaginatedData={fetchPaginatedData}
          accountNameFilter={accountNameFilter}
          filteredCount={filteredCount}
          totalFilteredCount={totalFilteredCount}
          hasActiveFilters={hasActiveFilters}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 2, mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {renderContent()}
    </Paper>
  );
};

PaginationControls.propTypes = {
  pagination: PropTypes.object,
  isLoadingPaginated: PropTypes.bool.isRequired,
  fetchPaginatedData: PropTypes.func.isRequired,
  accountNameFilter: PropTypes.string.isRequired,
  filteredCount: PropTypes.number.isRequired,
  totalFilteredCount: PropTypes.number,
  hasActiveFilters: PropTypes.bool.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageSizeChange: PropTypes.func.isRequired
};

export default PaginationControls;